import { useEffect, useId, useMemo, useRef, type MutableRefObject } from 'react'
import { axisTicks, overruns, sampleTrack, type Track } from './chart'
import { onFrame } from './ticker'

/**
 * Ein Diagramm über den Gangzyklus — SVG, keine Bibliothek.
 *
 * WARUM SELBST GEZEICHNET: eine Diagrammbibliothek bringt ihr eigenes
 * Farbsystem, ihre eigenen Achsen und ihre eigene Typografie mit. Alles drei
 * müsste man ihr wieder abgewöhnen, und die Marke hängt hier an drei Tokens.
 * Ein Liniendiagramm über eine Zeitreihe sind dreissig Zeilen Pfadberechnung;
 * eine Bibliothek dafür wäre 200 kB für Arbeit, die niemand abnimmt.
 *
 * SVG und nicht Canvas, weil das Diagramm bei jeder Bildschirmauflösung scharf
 * bleibt und der Text darin vom Bildschirmleser gefunden wird.
 *
 * KEIN `preserveAspectRatio="none"`. Es füllt zwar jede Breite, staucht dabei
 * aber die Beschriftung mit — Achsenzahlen werden schmal und unlesbar, und die
 * Strichstärke wird richtungsabhängig. Stattdessen behält das Bild sein
 * Seitenverhältnis und die Kachel richtet sich danach.
 *
 * VIER SORTEN VON HINTERGRUND, die man nicht verwechseln darf:
 *
 *   Standphase   — Zeit. Wann trägt der Fuss? Ein Feld über die Breite.
 *   Normband     — Streuung. Wo lag die Aufzeichnung? Ein Schlauch um eine Kurve.
 *   Grenze       — Mechanik. Was hält die Achse aus? Zwei waagerechte Linien.
 *   Überschreitung — Befund. Wo hält sie es NICHT aus? Rote Streifen.
 *
 * Sie in einem Ton zu zeichnen wäre bequemer und würde die einzige Frage
 * verdecken, wegen der jemand ein Diagramm überhaupt aufmacht: hält das?
 */

export interface Series {
  key: string
  label: string
  /** Werte über den Zyklus. Länge bestimmt die Auflösung. */
  values: Track
  /**
   * Welche der sechs Serienfarben.
   *
   * Eine Nummer und kein Farbwert: die Farben stehen als semantische Tokens in
   * `tokens.css` und sind in beiden Registern getrennt gesetzt. Eine Komponente,
   * die ihre eigene Farbe mitbringt, bricht beim ersten Registerwechsel.
   */
  tone: 1 | 2 | 3 | 4 | 5 | 6
  /** Gestrichelt: für Vergleichs- und Grenzlinien. */
  dashed?: boolean
}

export interface Band {
  /** Untere und obere Grenze — der graue Korridor. */
  lo: number
  hi: number
  label: string
}

/**
 * Die mechanische Grenze der Achse.
 *
 * ETWAS ANDERES ALS EIN BAND, und deshalb ein eigener Eingang. Ein Band ist
 * Kontext — hier liegt der übliche Bereich. Eine Grenze ist eine Zusage der
 * Konstruktion, und wo die Kurve sie verlässt, ist das kein Kontext mehr,
 * sondern ein Befund. Er wird rot markiert, weil er genau das ist.
 */
export interface Limit {
  lo: number
  hi: number
  label: string
}

/**
 * Das Normband: eine Kurve mit Streuung, nicht zwei waagerechte Linien.
 *
 * Es beantwortet die Frage, die eine gerechnete Winkelkurve allein nicht
 * beantwortet — liegt sie noch in dem, was gemessen wurde? Ein konstantes Band
 * könnte das nicht ausdrücken: die Streuung der Aufzeichnung ist über den
 * Zyklus zwar konstant, ihr Mittelwert aber gerade nicht.
 */
export interface Corridor {
  mean: Track
  /** Streuung in der Einheit der Kurve. Das Band ist Mittelwert ± sd. */
  sd: number
  label: string
}

export interface CycleChartProps {
  title: string
  unit: string
  series: Series[]
  /** Waagerechte Grenzlinien, etwa der Bewegungsbereich einer Achse. */
  band?: Band
  /** Die mechanische Grenze — mit rot markierter Überschreitung. */
  limit?: Limit
  /** Das Normband der Aufzeichnung, als Schlauch um seinen Mittelwert. */
  corridor?: Corridor
  /** Anteil des Zyklus, den die Standphase einnimmt — als Hintergrundfeld. */
  stance?: number
  /** Höhe in Pixeln. */
  height?: number
  /** Nullinie immer zeigen, auch wenn die Werte sie nicht schneiden. */
  zero?: boolean
  /**
   * Wofür die waagerechte Achse steht.
   *
   * Vorbelegt mit dem Gangzyklus in Prozent — das ist der Normalfall. Die
   * Massenbilanz trägt dieselbe Kurvenform über der MASSE auf; ohne diese
   * Angabe stünde dort „0 … 100 %", und das wäre schlicht falsch beschriftet.
   */
  xDomain?: [number, number]
  /** Einheit der waagerechten Achse. */
  xUnit?: string
  /** Wie die waagerechte Achse im Vorlesetext heisst. */
  xName?: string
  /**
   * Beschriftung der waagerechten Achse. Die Einheit kommt aus `xUnit`.
   */
  xLabel?: string
  /**
   * Beschriftung der senkrechten Achse. Die Einheit kommt aus `unit`.
   *
   * Ohne Angabe aus dem Titel abgeleitet: „Knie · Moment" ergibt „Moment". Ein
   * Diagramm ohne Achsbezeichnung zwingt zum Raten, welche Grösse aufgetragen
   * ist.
   */
  yLabel?: string
  /**
   * Senkrechte Marken, als Anteil 0 … 1 der Breite.
   *
   * Der Zeitschrieb braucht sie: ohne Zyklusgrenzen läuft die Kurve durch und
   * man sieht nicht, wo ein Schritt aufhört. `change` markiert, wo jemand
   * etwas umgestellt hat — das ist die interessantere der beiden Sorten.
   */
  marks?: { at: number; kind: 'cycle' | 'change'; label?: string }[]
  /**
   * Die Abspielmarke — dieselbe Referenz, aus der die Bühne ihre Haltung nimmt.
   *
   * SIE IST DER GRUND, WARUM BÜHNE UND DIAGRAMM ZUSAMMEN ETWAS TAUGEN. Ohne sie
   * stehen links eine Figur und rechts eine Kurve, und man muss im Kopf
   * verbinden, welche Stelle der Kurve zu der Haltung gehört, die man gerade
   * sieht. Mit ihr zeigt das Diagramm auf genau diese Stelle — und die Zahl
   * daneben ist die, die die Figur gerade fährt.
   *
   * Eine REFERENZ und kein Wert: sie ändert sich sechzigmal je Sekunde. Als
   * Eigenschaft gereicht, würde jedes Bild einen Renderdurchlauf auslösen.
   * Stattdessen wird die Marke im Bildtakt direkt am Knoten nachgezogen.
   */
  cursor?: MutableRefObject<number>
  /** Nachkommastellen für die abgelesenen Werte. */
  decimals?: number
  /** Zeile unter dem Bild mit Wertebereich und Achsenbezeichnung. */
  foot?: boolean
}

const PAD = { left: 62, right: 14, top: 10, bottom: 40 }

export function CycleChart({
  title,
  unit,
  series,
  band,
  limit,
  corridor,
  stance,
  height = 180,
  zero = true,
  xDomain = [0, 100],
  xUnit = '%',
  xName = 'den Gangzyklus',
  xLabel = 'Gangzyklus',
  yLabel,
  marks,
  cursor,
  decimals = 1,
  foot = true,
}: CycleChartProps) {
  const id = useId()
  const width = 640
  const plotWidth = width - PAD.left - PAD.right
  const plotHeight = height - PAD.top - PAD.bottom

  const scale = useMemo(() => {
    let lo = Infinity
    let hi = -Infinity
    for (const s of series) {
      for (const v of s.values) {
        if (!Number.isFinite(v)) continue
        if (v < lo) lo = v
        if (v > hi) hi = v
      }
    }
    if (band) {
      lo = Math.min(lo, band.lo)
      hi = Math.max(hi, band.hi)
    }
    /*
      DIE GRENZE ZIEHT DIE SKALA NICHT AUF. Sie liegt bei einigen Achsen um ein
      Vielfaches über dem, was die Bewegung verlangt — nähme man sie in den
      Bereich auf, läge die Kurve als flache Linie am Boden eines leeren Bildes.
      Die Grenzlinie wird gezeichnet, wenn sie hineinpasst; die Überschreitung
      ist ohnehin an den roten Streifen zu sehen und nicht an der Linie.
    */
    if (corridor) {
      for (const v of corridor.mean) {
        if (!Number.isFinite(v)) continue
        if (v - corridor.sd < lo) lo = v - corridor.sd
        if (v + corridor.sd > hi) hi = v + corridor.sd
      }
    }
    if (zero) {
      lo = Math.min(lo, 0)
      hi = Math.max(hi, 0)
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { lo: 0, hi: 1 }
    if (hi - lo < 1e-9) return { lo: lo - 1, hi: hi + 1 }
    /* Ein Zehntel Luft nach oben und unten, damit die Spitzen nicht am Rand kleben. */
    const margin = (hi - lo) * 0.1
    return { lo: lo - margin, hi: hi + margin }
  }, [series, band, corridor, zero])

  const x = (i: number, n: number) => PAD.left + (i / (n - 1)) * plotWidth
  const xAt = (t: number) => PAD.left + t * plotWidth
  const y = (v: number) =>
    PAD.top + plotHeight - ((v - scale.lo) / (scale.hi - scale.lo)) * plotHeight

  const path = (values: Track, offset = 0): string => {
    const n = values.length
    let d = ''
    for (let i = 0; i < n; i++) {
      const v = values[i]
      if (!Number.isFinite(v)) continue
      d += `${d ? 'L' : 'M'}${x(i, n).toFixed(2)} ${y(v + offset).toFixed(2)}`
    }
    return d
  }

  /* Der Schlauch: obere Kante hin, untere Kante zurück, geschlossen. */
  const corridorPath = useMemo(() => {
    if (!corridor) return ''
    const n = corridor.mean.length
    if (n < 2) return ''
    let d = ''
    for (let i = 0; i < n; i++) {
      const v = corridor.mean[i] + corridor.sd
      d += `${i === 0 ? 'M' : 'L'}${x(i, n).toFixed(2)} ${y(v).toFixed(2)}`
    }
    for (let i = n - 1; i >= 0; i--) {
      const v = corridor.mean[i] - corridor.sd
      d += `L${x(i, n).toFixed(2)} ${y(v).toFixed(2)}`
    }
    return `${d}Z`
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- x und y hängen an scale */
  }, [corridor, scale, plotWidth, plotHeight])

  /* Aus dem Titel abgeleitet: „Knie · Moment" ergibt „Moment". Ein Titel ohne
     Trenner steht für sich. */
  const yName = yLabel ?? title.split('·').pop()?.trim() ?? ''

  const ticks = useMemo(() => axisTicks(scale.lo, scale.hi), [scale])
  const tickDecimals = ticks.length > 1 && Math.abs(ticks[1] - ticks[0]) < 1 ? 1 : 0

  /*
    Die Überschreitungen werden EINMAL je Kurve gerechnet und nicht je Bild:
    sie hängen an den Werten und an der Grenze, und beide stehen still, solange
    niemand am Modell dreht.
  */
  const breaches = useMemo(() => {
    if (!limit) return []
    const out: { key: string; from: number; to: number }[] = []
    for (const s of series) {
      for (const run of overruns(s.values, limit.lo, limit.hi)) {
        out.push({ key: s.key, from: run.from, to: run.to })
      }
    }
    return out
  }, [series, limit])

  /* ------------------------------------------------------------------
     Die Abspielmarke, im Bildtakt und ohne Renderdurchlauf
     ------------------------------------------------------------------ */

  const headLine = useRef<SVGLineElement | null>(null)
  const headDots = useRef<(SVGCircleElement | null)[]>([])
  const readouts = useRef<(HTMLSpanElement | null)[]>([])

  useEffect(() => {
    if (!cursor) return
    let last = -1
    return onFrame(() => {
      const t = Math.min(1, Math.max(0, cursor.current))
      /* Nichts anfassen, solange sich die Stelle nicht bewegt hat — bei
         angehaltener Wiedergabe kostet die Seite dann nichts. */
      if (Math.abs(t - last) < 1e-4) return
      last = t
      const px = xAt(t)
      const line = headLine.current
      if (line) {
        line.setAttribute('x1', String(px))
        line.setAttribute('x2', String(px))
      }
      series.forEach((s, i) => {
        const value = sampleTrack(s.values, t)
        const dot = headDots.current[i]
        if (dot) {
          const ok = Number.isFinite(value)
          dot.setAttribute('cx', String(px))
          dot.setAttribute('cy', ok ? String(y(value)) : '-99')
          dot.setAttribute('opacity', ok ? '1' : '0')
        }
        const out = readouts.current[i]
        if (out) {
          out.textContent = Number.isFinite(value)
            ? `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(decimals)}`
            : '–'
        }
      })
    })
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- xAt und y hängen an scale */
  }, [cursor, series, scale, decimals, plotWidth, plotHeight])

  /* Die Legende steht, sobald es etwas zu unterscheiden gibt — oder sobald sie
     etwas anzeigt, was ohne sie nirgends stünde. */
  const legend = series.length > 1 || Boolean(cursor)

  return (
    <figure className="chart">
      <figcaption className="chart__head">
        <span className="chart__title">{title}</span>
        <span className="chart__unit numeric">{unit}</span>
      </figcaption>

      <svg
        className="chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${title} über ${xName}, ${unit}`}
      >
        {/* Standphase als Feld — sie ordnet jede Kurve zeitlich ein. */}
        {stance !== undefined && stance > 0 && stance < 1 && (
          <>
            <rect
              className="chart__stance"
              x={PAD.left}
              y={PAD.top}
              width={plotWidth * stance}
              height={plotHeight}
            />
            {/*
              Die Kante des Feldes zusätzlich als Strich. Das Feld allein ist so
              schwach, dass sein Ende auf einem hellen Schirm verschwindet — und
              genau dieses Ende ist der Zeitpunkt, an dem der Fuss abhebt.
            */}
            <line
              className="chart__toeoff"
              x1={xAt(stance)}
              x2={xAt(stance)}
              y1={PAD.top}
              y2={PAD.top + plotHeight}
            />
          </>
        )}

        {band && (
          <rect
            className="chart__band"
            x={PAD.left}
            y={y(band.hi)}
            width={plotWidth}
            height={Math.max(1, y(band.lo) - y(band.hi))}
          />
        )}

        {corridorPath && <path className="chart__corridor" d={corridorPath} />}

        {/* Überschreitungen liegen UNTER dem Gitter: sie sind Fläche, kein Text. */}
        {breaches.map((run, i) => (
          <rect
            key={`${id}-breach-${i}`}
            className="chart__breach"
            x={xAt(run.from)}
            y={PAD.top}
            width={Math.max(1.5, (run.to - run.from) * plotWidth)}
            height={plotHeight}
          />
        ))}

        {ticks.map((v) => (
          <g key={v}>
            <line
              className={v === 0 ? 'chart__zero' : 'chart__grid'}
              x1={PAD.left}
              x2={width - PAD.right}
              y1={y(v)}
              y2={y(v)}
            />
            <text className="chart__label numeric" x={PAD.left - 6} y={y(v) + 3.5}>
              {v.toFixed(tickDecimals)}
            </text>
          </g>
        ))}

        {/* Senkrechte Viertelmarken. Ohne sie schätzt man die Stelle einer
            Spitze am unteren Rand ab, statt sie abzulesen. */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={`${id}-v-${f}`}
            className="chart__grid"
            x1={xAt(f)}
            x2={xAt(f)}
            y1={PAD.top}
            y2={PAD.top + plotHeight}
          />
        ))}

        {corridorPath && (
          <path className="chart__corridor-mean" d={path(corridor?.mean ?? [])} />
        )}

        {/* Die Grenzen zuletzt unter den Kurven: sie sollen sichtbar sein, aber
            keine Kurve verdecken. */}
        {limit &&
          [limit.lo, limit.hi].map((v, i) =>
            v > scale.lo && v < scale.hi ? (
              <line
                key={`${id}-limit-${i}`}
                className="chart__limit"
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(v)}
                y2={y(v)}
              />
            ) : null,
          )}

        {[0, 25, 50, 75, 100].map((p) => {
          const value = xDomain[0] + ((xDomain[1] - xDomain[0]) * p) / 100
          const step = (xDomain[1] - xDomain[0]) / 4
          return (
            <text
              key={p}
              className="chart__label numeric"
              x={PAD.left + (p / 100) * plotWidth}
              y={height - PAD.bottom + 14}
              textAnchor="middle"
            >
              {step >= 1 ? value.toFixed(0) : value.toFixed(2)}
            </text>
          )
        })}

        {/* Achsbezeichnungen. Eine Achse ohne Grösse und Einheit zwingt zum
            Raten, was aufgetragen ist. */}
        <text
          className="chart__axis"
          x={PAD.left + plotWidth / 2}
          y={height - 5}
          textAnchor="middle"
        >
          {xLabel}
          {xUnit ? ` · ${xUnit}` : ''}
        </text>
        <text
          className="chart__axis"
          transform={`translate(13 ${PAD.top + plotHeight / 2}) rotate(-90)`}
          textAnchor="middle"
        >
          {yName}
          {unit ? ` · ${unit}` : ''}
        </text>

        {marks?.map((mark, i) => {
          const at = PAD.left + mark.at * plotWidth
          return (
            <g key={`${id}-mark-${i}`}>
              <line
                className={`chart__mark chart__mark--${mark.kind}`}
                x1={at}
                x2={at}
                y1={PAD.top}
                y2={PAD.top + plotHeight}
              />
              {mark.label && (
                <text className="chart__mark-label" x={at + 3} y={PAD.top + 9}>
                  {mark.label}
                </text>
              )}
            </g>
          )
        })}

        {series.map((s) => (
          <path
            key={`${id}-${s.key}`}
            className={`chart__line${s.dashed ? ' chart__line--dashed' : ''}`}
            style={{ stroke: `var(--chart-${s.tone})` }}
            d={path(s.values)}
          />
        ))}

        {cursor && (
          <g className="chart__head-group">
            <line
              ref={headLine}
              className="chart__playhead"
              x1={PAD.left}
              x2={PAD.left}
              y1={PAD.top}
              y2={PAD.top + plotHeight}
            />
            {series.map((s, i) => (
              <circle
                key={`${id}-dot-${s.key}`}
                ref={(node) => {
                  headDots.current[i] = node
                }}
                className="chart__dot"
                r={3.2}
                cx={PAD.left}
                cy={-99}
                style={{ fill: `var(--chart-${s.tone})` }}
              />
            ))}
          </g>
        )}
      </svg>

      {legend && (
        <ul className="chart__legend">
          {series.map((s, i) => (
            <li key={s.key} className="chart__legend-item">
              <span className="chart__swatch" style={{ background: `var(--chart-${s.tone})` }} />
              {s.label}
              {cursor && (
                <span
                  ref={(node) => {
                    readouts.current[i] = node
                  }}
                  className="chart__value numeric"
                >
                  –
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {foot && (
        <div className="chart__foot">
          <span className="numeric">
            {ticks.length > 0
              ? `${ticks[0].toFixed(tickDecimals)} … ${ticks[ticks.length - 1].toFixed(tickDecimals)} ${unit}`
              : unit}
          </span>
          <span>
            {corridor ? `${corridor.label} · ` : ''}
            {limit ? `${limit.label} · ` : ''}
            {xDomain[0]} – {xDomain[1]} {xUnit}
          </span>
        </div>
      )}
    </figure>
  )
}
